import { button, el } from './dom';
import { t } from '../i18n';

/**
 * Header install control. It reflects whether the PWA is installed and offers
 * installation when it is not.
 *
 * Detection: `display-mode: standalone` only reports true inside the installed
 * app window, so from a normal tab we also ask `getInstalledRelatedApps()`
 * (Chrome/Edge) — with the app listed in the manifest's related_applications,
 * that reports this PWA as installed even from a tab, and is re-checked when the
 * tab regains focus.
 *
 * There is deliberately no "uninstall" action: browsers expose no API for a page
 * to uninstall itself, so the installed state shows how to remove it instead.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
type NavWithRelated = Navigator & {
  getInstalledRelatedApps?: () => Promise<Array<{ platform: string; url?: string }>>;
};

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

async function detectInstalled(): Promise<boolean> {
  if (isStandalone()) return true;
  const nav = navigator as NavWithRelated;
  if (nav.getInstalledRelatedApps) {
    try {
      const apps = await nav.getInstalledRelatedApps();
      if (apps.some((a) => a.platform === 'webapp')) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

export function installInstallButton(): void {
  const btn = button('installBtn');
  const hint = el('installHint');
  let deferred: BeforeInstallPromptEvent | null = null;
  let installed = false;

  const render = (): void => {
    btn.disabled = false;
    if (installed) {
      btn.textContent = t('install.installed');
      btn.classList.add('installed');
      btn.title = t('install.uninstallHint');
    } else {
      btn.textContent = t('install.prompt');
      btn.classList.remove('installed');
      btn.title = t('install.promptTitle');
    }
  };

  const refreshInstalled = async (): Promise<void> => {
    const before = installed;
    installed = await detectInstalled();
    if (installed !== before) {
      hint.hidden = true;
      render();
    }
  };

  render();
  void refreshInstalled();

  // NOTE: we deliberately do NOT call e.preventDefault() here. Preventing it
  // suppresses the browser's own native install offer (mini-infobar / prompt);
  // letting it through keeps that native affordance while we still capture the
  // event so our header button can trigger installation as a backup.
  window.addEventListener('beforeinstallprompt', (e) => {
    deferred = e as BeforeInstallPromptEvent;
    installed = false; // the browser only fires this when NOT installed
    render();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    hint.hidden = true;
    render();
  });

  // Install/uninstall can happen outside the page; re-check on return.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void refreshInstalled();
  });
  window.addEventListener('focus', () => void refreshInstalled());

  btn.addEventListener('click', async () => {
    if (installed) {
      hint.textContent = t('install.uninstallHint');
      hint.hidden = !hint.hidden;
      return;
    }
    if (deferred) {
      btn.disabled = true;
      hint.hidden = true;
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === 'accepted') {
          deferred = null;
          installed = true;
        }
      } catch {
        /* ignore */
      }
      render();
      return;
    }
    // No automatic prompt available — show manual instructions.
    hint.textContent = t('install.manualHint');
    hint.hidden = !hint.hidden;
  });
}
